<script setup lang="ts">
import { computed, h, ref } from 'vue'

import { Plus } from '@lucide/vue'
import {
  type ColumnDef,
  createSortedRowModel,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_datetime,
  tableFeatures,
  useTable,
} from '@tanstack/vue-table'
import { RouterLink } from 'vue-router'

import CreateCustomerDialog from '#customers/components/CreateCustomerDialog.vue'
import { currencyLabel } from '#customers/lib/currencies'
import DataTable from '#shared/components/DataTable.vue'
import ListToolbar from '#shared/components/ListToolbar.vue'
import PageHeader from '#shared/components/PageHeader.vue'
import SearchInput from '#shared/components/SearchInput.vue'
import SortableHeader from '#shared/components/SortableHeader.vue'
import { useOrganizations } from '#shared/composables/use-organizations'
import type { Organization } from '#shared/composables/use-organizations'
import { Button } from '#ui/button'
import { ICONS } from '#utils/icons'
import { ROW_CLASS, ROW_LINK_CLASS } from '#utils/table'

definePage({
  meta: { requiresAuth: true, title: 'Customers' },
})

const search = ref('')
const createOpen = ref(false)

const orgsQuery = useOrganizations()

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  const list = orgsQuery.data.value ?? []
  const matched = q
    ? list.filter(
        (o) => o.name.toLowerCase().includes(q) || o.organizationId.toLowerCase().includes(q),
      )
    : list
  return [...matched].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
  )
})

const features = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  sortFns: { alphanumeric: sortFn_alphanumeric, datetime: sortFn_datetime },
})

const columns: ColumnDef<typeof features, Organization>[] = [
  {
    accessorKey: 'name',
    header: ({ column }) =>
      h(SortableHeader, {
        label: 'Name',
        sorted: column.getIsSorted(),
        onToggle: column.getToggleSortingHandler(),
      }),
    cell: ({ row }) =>
      h(
        RouterLink,
        {
          to: `/customers/${row.original.organizationId}`,
          class: `font-medium ${ROW_LINK_CLASS}`,
        },
        () => row.original.name,
      ),
  },
  {
    accessorKey: 'defaultCurrency',
    header: 'Default currency',
    cell: ({ row }) =>
      h(
        'span',
        { class: 'text-sm text-muted-foreground' },
        currencyLabel(row.original.defaultCurrency),
      ),
  },
  {
    accessorKey: 'organizationId',
    header: 'Organization ID',
    cell: ({ row }) =>
      h('span', { class: 'font-mono text-xs text-muted-foreground' }, row.original.organizationId),
  },
]

const table = useTable({
  features,
  data: computed(() => filtered.value),
  columns,
})
</script>

<template>
  <PageHeader title="Customers">
    <template #actions>
      <Button @click="createOpen = true">
        <Plus />
        New customer
      </Button>
    </template>
  </PageHeader>

  <div class="flex flex-col gap-3">
    <ListToolbar
      noun="customers"
      singular="customer"
      :shown="orgsQuery.isSuccess.value ? filtered.length : undefined"
      :total="orgsQuery.data.value?.length"
    >
      <SearchInput v-model="search" label="Search customers" placeholder="Search by name or ID…" />
    </ListToolbar>

    <DataTable
      :table="table"
      :is-loading="orgsQuery.isLoading.value"
      :error="orgsQuery.error.value"
      :filtered="!!search.trim()"
      noun="customers"
      :icon="ICONS.customers"
      :row-class="ROW_CLASS"
      @clear="search = ''"
    >
      <template #empty-description>
        A customer is an organization with its own data, members and sign-in.
      </template>
      <template #empty-action>
        <Button @click="createOpen = true">
          <Plus />
          New customer
        </Button>
      </template>
    </DataTable>
  </div>

  <CreateCustomerDialog v-model:open="createOpen" />
</template>
